// src/pages/Home.jsx
import React, { Fragment, useEffect, useRef, useState, useCallback, useContext } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Appfooter from "../components/Appfooter";
import Postview from "../components/Postview";
import { makeStyles } from "@material-ui/core/styles";
import { useAPI } from "../context/ApiContext";
import { useTranslation } from "react-i18next";
import { SnackbarContext } from "../context/SnackbarContext";
import AdvertisementInline from "../components/AdvertisementInline";
import { Helmet } from "react-helmet";

const useStyles = makeStyles(() => ({
  line: { display: "block", position: "relative" },
}));

const TABS = [
  { id: "for_you", labelKey: "HomeForYou", fallback: "For you" },
  { id: "following", labelKey: "HomeFollowing", fallback: "Following" },
];

const Home = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { newSnack } = useContext(SnackbarContext);

  // --- Appバナー表示条件 & 永続抑止 ---
const APP_STORE_URL = "https://apps.apple.com/jp/app/nicelinks/id6754094457";
const DISMISS_KEY = "nl_app_promo_dismissed_v1";

const isIOS = () => /iP(hone|ad|od)/.test(navigator.userAgent);
const isAndroid = () => /Android/i.test(navigator.userAgent);
const isMobileUA = () => isIOS() || isAndroid();
// 独自アプリ内WebView（UAに自アプリ名を含める場合はここで除外）
const isInAppWebView = () => /NicelinksApp/i.test(navigator.userAgent) || window.ReactNativeWebView;
// PWAやスタンドアロン表示なら非表示
const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone;

const shouldShowAppPromo = () =>
  isMobileUA() && !isInAppWebView() && !isStandalone() && !localStorage.getItem(DISMISS_KEY);

  const [showAppPromo, setShowAppPromo] = useState(false);
useEffect(() => { setShowAppPromo(shouldShowAppPromo()); }, []);

const dismissAppPromo = useCallback(() => {
  localStorage.setItem(DISMISS_KEY, "1");
  setShowAppPromo(false);
}, []);


  const { isAuthenticated, getTimeline, getTimelineFollowing, listAds } = useAPI();

  // ---- タブ状態 ----
  const [active, setActive] = useState("for_you"); // "for_you" | "following"
  // ゲストは For you 固定
  useEffect(() => {
    if (!isAuthenticated && active !== "for_you") setActive("for_you");
  }, [isAuthenticated, active]);

  // ---- TL 状態（タブごとに独立管理） ----
  const initState = { items: [], page: 0, loading: false, hasMore: true, didFirst: false };
  const [forYou, setForYou] = useState(initState);
  const [following, setFollowing] = useState(initState);

  // ---- 広告 ----
  const [ads, setAds] = useState([]);

  // ---- アダプタ（旧 Postview に合わせる）----
  const adaptPost = useCallback((p) => ({
    id: p.postId,
    userPostProfile: p.profile,
    reposting: p.isRepostItem ? true : null,
    repostingProfile: p.repostingProfile || null,
    commentedCounts: p.commentedCount,
    repostedTimes: p.repostedCount,
    likedTimes: p.niceCount,
    __raw: p,
  }), []);

  // ---- 無限スクロール監視（タブごと）----
  const loaderRef = useRef(null);
  const onIntersect = useCallback(([entry]) => {
    if (!entry.isIntersecting) return;
    // アクティブなタブの状態を見て次ページ要求
    if (active === "for_you") {
      if (!forYou.didFirst || forYou.loading || !forYou.hasMore) return;
      setForYou((s) => ({ ...s, page: s.page + 1 }));
    } else {
      if (!following.didFirst || following.loading || !following.hasMore) return;
      setFollowing((s) => ({ ...s, page: s.page + 1 }));
    }
  }, [active, forYou, following]);

  useEffect(() => {
    const io = new IntersectionObserver(onIntersect, { root: null, rootMargin: "400px 0px 0px 0px", threshold: 0.01 });
    const el = loaderRef.current;
    if (el) io.observe(el);
    return () => io.disconnect();
  }, [onIntersect]);

  // ---- 1ページ取得（タブ別）----
  const PAGE_SIZE = 20;

  const fetchPageForYou = useCallback(async (page) => {
    setForYou((s) => ({ ...s, loading: true }));
    try {
      const data = await getTimeline({ count: page });
      const list = Array.isArray(data?.res) ? data.res : [];
      const adapted = list.map(adaptPost);
      setForYou((s) => ({
        ...s,
        items: page === 0 ? adapted : [...s.items, ...adapted],
        hasMore: adapted.length > 0 && ((page + 1) * PAGE_SIZE) < (Number(data?.totalCount) || Infinity),
        loading: false,
        didFirst: true,
      }));
    } catch {
      setForYou((s) => ({ ...s, loading: false, hasMore: false, didFirst: true }));
      newSnack("error", t("LoadFailed") || "Failed to load");
    }
  }, [getTimeline, adaptPost, newSnack, t]);

  const fetchPageFollowing = useCallback(async (page) => {
    setFollowing((s) => ({ ...s, loading: true }));
    try {
      const data = await getTimelineFollowing({ count: page });
      const list = Array.isArray(data?.res) ? data.res : [];
      const adapted = list.map(adaptPost);
      setFollowing((s) => ({
        ...s,
        items: page === 0 ? adapted : [...s.items, ...adapted],
        hasMore: adapted.length > 0 && ((page + 1) * PAGE_SIZE) < (Number(data?.totalCount) || Infinity),
        loading: false,
        didFirst: true,
      }));
    } catch {
      setFollowing((s) => ({ ...s, loading: false, hasMore: false, didFirst: true }));
      newSnack("error", t("LoadFailed") || "Failed to load");
    }
  }, [getTimelineFollowing, adaptPost, newSnack, t]);

  // ---- タブ切替時：未ロードなら1ページ目 ----
  useEffect(() => {
    if (active === "for_you") {
      if (!forYou.didFirst && !forYou.loading) fetchPageForYou(0);
    } else {
      if (isAuthenticated && !following.didFirst && !following.loading) fetchPageFollowing(0);
    }
  }, [active, forYou.didFirst, forYou.loading, following.didFirst, following.loading, fetchPageForYou, fetchPageFollowing, isAuthenticated]);

  // ---- ページが進んだら追撃ロード ----
  useEffect(() => { if (forYou.page > 0) fetchPageForYou(forYou.page); }, [forYou.page, fetchPageForYou]);
  useEffect(() => { if (following.page > 0) fetchPageFollowing(following.page); }, [following.page, fetchPageFollowing]);

  // ---- 広告 ----
  const fetchAds = useCallback(async () => {
    try {
      const data = await listAds({});
      const array = Array.isArray(data) ? data : Array.isArray(data?.res) ? data.res : [];
      setAds(array);
    } catch { setAds([]); }
  }, [listAds]);
  useEffect(() => { fetchAds(); }, [fetchAds]);

  // ---- スワイプ（シンプルな左右スワイプでタブ切替）----
  const touch = useRef({ x: 0, y: 0, active: false });
  const onTouchStart = (e) => {
    const t0 = e.touches?.[0];
    if (!t0) return;
    touch.current = { x: t0.clientX, y: t0.clientY, active: true };
  };
  const onTouchMove = (e) => {
    // ここでは判定しない
  };
  const onTouchEnd = (e) => {
    if (!touch.current.active) return;
    const t0 = e.changedTouches?.[0];
    if (!t0) return;
    const dx = t0.clientX - touch.current.x;
    const dy = t0.clientY - touch.current.y;
    touch.current.active = false;
    // 横方向に十分スワイプ & 縦成分は小さめ
    if (Math.abs(dx) > 60 && Math.abs(dy) < 40) {
      if (dx < 0) {
        // 右→左：次タブへ
        if (isAuthenticated && active === "for_you") setActive("following");
      } else {
        // 左→右：前タブへ
        if (active === "following") setActive("for_you");
      }
    }
  };

  // ---- 広告差し込み描画 ----
  const renderWithAds = (items) => {
    const nodes = [];
    const adTotal = ads.length;
    let adIdx = 0;

    items.forEach((p, index) => {
      nodes.push(
        <Postview
          key={`pv-${p.id}-${index}`}
          postData={p.__raw}
          profileData={p.userPostProfile}
          reposting={p.reposting}
          repostingProfileData={p.repostingProfile}
          commentedCounts={p.commentedCounts}
          repostedTimes={p.repostedTimes}
          likedTimes={p.likedTimes}
          monopage={p.reviewToPage}
          onDeleted={(deletedId) => {
            // 両タブから消す
            setForYou((s) => ({ ...s, items: s.items.filter(x => x.id !== deletedId) }));
            setFollowing((s) => ({ ...s, items: s.items.filter(x => x.id !== deletedId) }));
          }}
        />
      );

      if (adTotal > 0 && (index + 1) % 5 === 0) {
        const ad = ads[adIdx % adTotal];
        nodes.push(<AdvertisementInline ad={ad} key={`ad-${index}-${ad?.id || adIdx}`} />);
        adIdx += 1;
      }
    });

    return nodes;
  };

  // ---- 表示データ ----
  const activeState = active === "for_you" ? forYou : following;
  const title = active === "for_you"
    ? (t("HomeForYou") || "For you")
    : (t("HomeFollowing") || "Following");

  return (
    <Fragment>
      <Helmet>
        <title>Nicelinks | Home</title>
        <meta property="og:title" content="Nicelinks | Home" />
        <meta name="twitter:title" content="Nicelinks | Home" />
        <meta name="apple-itunes-app" content="app-id=6754094457, affiliate-data=, app-argument=https://nicelinks.net/" />
      </Helmet>

      {/* バナー用の小さめCSS（崩れ対策） */}
      <style>{`
        .nl-promo-card{color:#fff;overflow:hidden;border:0}
        .nl-promo-body{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
        .nl-promo-icon{width:48px;aspect-ratio:1/1;border-radius:12px;display:inline-flex;justify-content:center;align-items:center;flex:0 0 auto;backdrop-filter:saturate(1.2)}
        .nl-promo-text{flex:1 1 auto;min-width:200px}
        .nl-promo-title{font-size:16px;font-weight:700}
        .nl-promo-lead{font-size:10px;opacity:.9}
        .nl-promo-cta{border:2px solid rgba(255,255,255,.95);color:#fff;background:transparent;border-radius:9999px;padding:6px 12px;font-size:10px;font-weight:800;line-height:1;white-space:nowrap;box-shadow:0 6px 18px rgba(0,0,0,.18)}
        @media (max-width: 640px){
          .nl-promo-cta{width:100%;text-align:center;order:3;margin-top:8px}
          .nl-promo-text{flex:1 1 100%;min-width:0}
        }
        .nl-tabs{display:flex;gap:20px;padding:12px 16px;border-bottom:1px solid #eee;position:sticky;top:56px;background:#fff;z-index:5}
        .nl-tab{font-weight:700;cursor:pointer;opacity:.6}
        .nl-tab.active{opacity:1;border-bottom:2px solid #111;padding-bottom:6px}

        /* App プロモバナー */
        .nl-app-promo {
          position: relative;
          overflow: hidden;
          border: 0;
          color: #0b1220;
          background: radial-gradient(120% 120% at 0% 0%, #dbeafe 0%, #bfdbfe 28%, #93c5fd 52%, #60a5fa 80%, #3b82f6 100%);
        }
        .nl-app-promo .nl-app-body{
          display:flex; align-items:center; gap:14px; flex-wrap:wrap;
        }
        .nl-app-promo .nl-app-icon{
          width:48px; height:48px; border-radius:12px; background:#fff;
          display:inline-flex; justify-content:center; align-items:center;
          box-shadow:0 8px 24px rgba(2,6,23,.12);
        }
        .nl-app-promo .nl-app-title{ font-weight:800; font-size:16px; letter-spacing:.01em; }
        .nl-app-promo .nl-app-lead{ font-size:12px; opacity:.9 }
        .nl-app-promo .nl-app-cta{
          display:inline-flex; align-items:center; gap:6px;
          background:#111827; color:#fff; border:0; border-radius:9999px;
          padding:8px 12px; font-weight:800; font-size:12px;
          box-shadow:0 10px 30px rgba(17,24,39,.25);
        }
        .nl-app-promo .nl-app-cta:hover{ opacity:.95 }
        .nl-app-promo .nl-app-close{
          position:absolute; right:10px; top:10px; border:0;
          width:28px; height:28px; border-radius:9999px; background:rgba(255,255,255,.7);
          display:inline-flex; align-items:center; justify-content:center;
        }
        @media (max-width:640px){
          .nl-app-promo .nl-app-cta{ width:100%; justify-content:center; }
        }

      `}</style>

      <Header />
      <Leftnav />

      <div className="main-content right-chat-active" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            {/* アプリ誘導バナー（モバイルのみ・閉じたら記憶） */}
{showAppPromo && (
  <a
    href={APP_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="text-decoration-none"
  >
    <div className="card w-100 shadow-xss rounded-xxl nl-app-promo mb-3">
      <div className="card-body nl-app-body">
        <div className="nl-app-icon">
          <i className="feather-smartphone" style={{ fontSize: 22 }} />
        </div>
        <div style={{ flex: "1 1 auto", minWidth: 200 }}>
          <div className="nl-app-title">
            {t("AppPromoTitle") || "アプリ版で快適に楽しもう！"}
          </div>
          <div className="nl-app-lead">
            {t("AppPromoLead") || "サクサク閲覧・プッシュ通知・ネイティブ共有に対応"}
          </div>
        </div>
        <button
          type="button"
          className="nl-app-cta"
          onClick={(e) => {
            // aタグの遷移を許可しつつ GA 等を入れるならここで
            e.stopPropagation(); // クリックの二重伝播防止
            // ここで特に何もしない（親<a>が遷移）
          }}
        >
          <i className="feather-download" /> {t("AppPromoCTA") || "App Storeで入手"}
        </button>
        <button
          type="button"
          aria-label="閉じる"
          className="nl-app-close"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismissAppPromo(); }}
          title={t("Close") || "閉じる"}
        >
          <i className="ti-close" />
        </button>
      </div>
    </div>
  </a>
)}


            {/* タブ */}
            <div className="nl-tabs">
              <div
                className={`nl-tab ${active === "for_you" ? "active" : ""}`}
                onClick={() => setActive("for_you")}
              >
                {t("HomeForYou") || "For you"}
              </div>
              {isAuthenticated && (
                <div
                  className={`nl-tab ${active === "following" ? "active" : ""}`}
                  onClick={() => setActive("following")}
                >
                  {t("HomeFollowing") || "Following"}
                </div>
              )}
            </div>

            {/* バナー */}
            {!isAuthenticated ? (
              <Link to="/howto" className="text-decoration-none">
                <div className="card w-100 shadow-xss rounded-xxl nl-promo-card mb-3" style={{ background: "linear-gradient(135deg,#111827,#1f2937)" }}>
                  <div className="card-body nl-promo-body">
                    <div className="nl-promo-icon" style={{ background: "rgba(255,255,255,.08)" }}>
                      <i className="feather-help-circle" style={{ fontSize: 22 }} />
                    </div>
                    <div className="nl-promo-text">
                      <div className="nl-promo-title">{t("Recommend1") || "How to use Nicelinks"}</div>
                      <div className="nl-promo-lead">{t("HomeHowtoLead") || "Nicelinksの基本的な使い方をチェック"}</div>
                    </div>
                  </div>
                </div>
              </Link>
            ) : (
              <Link to="/invite" className="text-decoration-none">
                <div className="card w-100 shadow-xss rounded-xxl nl-promo-card mb-3" style={{ background: "linear-gradient(135deg,#1d4ed8,#22c55e)" }}>
                  <div className="card-body nl-promo-body">
                    <div className="nl-promo-icon" style={{ background: "rgba(255,255,255,.15)" }}>
                      <i className="feather-user-plus" style={{ fontSize: 22 }} />
                    </div>
                    <div className="nl-promo-text">
                      <div className="nl-promo-title">{t("InviteBannerTitle") || "Nicelinksに友達を招待しよう！"}</div>
                      <div className="nl-promo-lead">{t("InviteBannerLead") || "招待リンクをコピーしてシェアすると、友達と今見ているページを一緒に楽しめます"}</div>
                    </div>
                    <span className="nl-promo-cta">{t("InviteCTA") || "招待ページへ"}</span>
                  </div>
                </div>
              </Link>
            )}

            {/* タブの中身 */}
            {renderWithAds(activeState.items)}

            {/* 無限スクロール監視ターゲット */}
            <div ref={loaderRef} style={{ height: 20, margin: "0 auto" }} className="mb-5" />

          </div>
          <div className="middle-sidebar-right d-none d-lg-block" />
        </div>
      </div>

      <Appfooter />
    </Fragment>
  );
};

export default Home;
